import { useState, useEffect } from 'react'
import { Mail, Check, X, Users, Clock, Building2 } from 'lucide-react'
import { getUserInvitations, acceptInvitation, declineInvitation } from '../../services/organizationService'
import { useAuthStore } from '../../store/authStore'
import { useToast } from '../../hooks/useToast'
import { useNavigate } from 'react-router-dom'
import Toast from '../../components/Toast'

export default function Invitations() {
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(null) // Track which invitation is being processed
  const { user } = useAuthStore()
  const { success: showSuccess, error: showError, toastState, closeToast } = useToast()
  const navigate = useNavigate()

  useEffect(() => {
    if (user?.email) {
      loadInvitations()
    }
  }, [user])

  const loadInvitations = async () => {
    try {
      const { data, error } = await getUserInvitations(user.email)
      if (error) throw error
      setInvitations(data || [])
    } catch (error) {
      console.error('Error loading invitations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptInvitation = async (invitationId, orgName) => {
    if (processing) return
    
    try {
      setProcessing(invitationId)
      const { error } = await acceptInvitation(invitationId)
      if (error) throw error
      
      showSuccess(`You've joined ${orgName}! Redirecting...`)
      
      // Reload invitations
      await loadInvitations()
      
      // Reload the page after a short delay to refresh organizations list
      setTimeout(() => {
        window.location.href = '/#/dashboard'
      }, 1500)
    } catch (error) {
      console.error('Error accepting invitation:', error)
      showError(error.message || 'Failed to accept invitation')
    } finally {
      setProcessing(null)
    }
  }

  const handleDeclineInvitation = async (invitationId, orgName) => {
    if (processing) return
    
    try {
      setProcessing(invitationId)
      const { error } = await declineInvitation(invitationId)
      if (error) throw error
      
      showSuccess(`Invitation from ${orgName} declined`)
      
      // Reload invitations
      await loadInvitations()
    } catch (error) {
      console.error('Error declining invitation:', error)
      showError(error.message || 'Failed to decline invitation')
    } finally {
      setProcessing(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 sm:py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-light dark:text-muted-dark">Loading invitations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Team Invitations</h1>
        <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark mt-1 sm:mt-2">
          Accept or decline team invitations
        </p>
      </div>

      {invitations.length === 0 ? (
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-8 sm:p-12 text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold mb-2">No pending invitations</h3>
          <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark">
            You don't have any team invitations at the moment.
          </p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {invitations.map((invitation) => {
            const isProcessing = processing === invitation.id
            const orgName = invitation.organizations?.name || 'Organization'
            
            return (
              <div 
                key={invitation.id} 
                className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  {/* Left side - Organization info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center text-white font-bold text-lg sm:text-xl shrink-0">
                        {orgName[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                          {orgName}
                        </h3>
                        {invitation.organizations?.slug && (
                          <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark mt-0.5 truncate">
                            @{invitation.organizations.slug}
                          </p>
                        )}
                        
                        <div className="mt-3 flex flex-wrap items-center gap-2 sm:gap-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            invitation.role === 'owner' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                            invitation.role === 'admin' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                            invitation.role === 'member' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                          }`}>
                            <Users className="w-3 h-3" />
                            {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                          </span>
                          
                          <span className="inline-flex items-center gap-1 text-xs text-muted-light dark:text-muted-dark">
                            <Building2 className="w-3 h-3" />
                            Invited {new Date(invitation.created_at).toLocaleDateString()}
                          </span>
                          
                          <span className="inline-flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400">
                            <Clock className="w-3 h-3" />
                            Expires {new Date(invitation.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - Actions */}
                  <div className="flex sm:flex-col gap-2 sm:ml-4">
                    <button
                      onClick={() => handleAcceptInvitation(invitation.id, orgName)}
                      disabled={isProcessing}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      {isProcessing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Accept
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleDeclineInvitation(invitation.id, orgName)}
                      disabled={isProcessing}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      <X className="w-4 h-4" />
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Toast Notifications */}
      <Toast
        isOpen={toastState.isOpen}
        message={toastState.message}
        type={toastState.type}
        onClose={closeToast}
      />
    </div>
  )
}
